import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LeafStateContext } from 'context/state/leaf';
import React, { useContext } from 'react';

import { LightState } from 'types/aspen/state';

import { adjustTextColor, createGroupGradient, getColorProperties } from 'modules/color';

import { LightsPowerSwitch } from '../PowerSwitch';
import { BrightnessSlider } from '../BrightnessSlider';

import styles from './groupLights.module.scss';

// Group light gradient constants
const GROUP_OVERLAY_GRADIENT = 'linear-gradient(to top, rgba(0,0,0,0.3) 50% , rgba(255,255,255,0.6) 95%)';

interface GroupProps {
  children: React.ReactNode;
}
export const GroupLightsTile = ({ children }: GroupProps) => {
  const { leaf } = useContext(LeafStateContext);
  const leafState = leaf.state as LightState;
  const brightnessLevel = leafState?.brightness?.level;
  const { colorRgb, brightnessFactor, powerState, colorType, temperatureState } = getColorProperties(
    leafState,
    brightnessLevel,
  );

  const groupLeaves =
    'memberLeaves' in leaf ? Object.values(leaf.memberLeaves).sort((a, b) => a.name.localeCompare(b.name)) : [];

  const groupGradient = createGroupGradient(groupLeaves);
  return (
    <Accordion
      className={styles.groupAccordion}
      sx={{
        borderRadius: 2,
      }}
    >
      <Stack
        direction="column"
        sx={{
          padding: 4,
          borderRadius: 2,
          position: 'relative',
          color: adjustTextColor(colorRgb, brightnessFactor, brightnessLevel, colorType, temperatureState, powerState),
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: groupGradient,
            filter: 'blur(5px)',
            transform: 'scale(1.05)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: GROUP_OVERLAY_GRADIENT,
            mixBlendMode: 'multiply',
          },
          '& > *': {
            position: 'relative',
            zIndex: 2,
          },
        }}
        className={styles.groupHeader}
      >
        <Box className={styles.gradientTextBackdrop}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontWeight: 500, letterSpacing: 0.5, lineHeight: 1.5, padding: 1 }} variant="h3">
              {leaf.name.toUpperCase()}
            </Typography>

            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{
                    color: adjustTextColor(
                      colorRgb,
                      brightnessFactor,
                      brightnessLevel,
                      colorType,
                      temperatureState,
                      powerState,
                    ),
                  }}
                />
              }
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <LightsPowerSwitch />
            </AccordionSummary>
          </Stack>

          <Box className={styles.brightnessSlider}>
            <BrightnessSlider />
          </Box>
        </Box>
      </Stack>

      <AccordionDetails className={styles.groupDetails}>{children}</AccordionDetails>
    </Accordion>
  );
};
